if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        // Update to use PUB_SUB_EVENTS
        window.addEventListener('DOMContentLoaded', () => {
          subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const { variant } = event.data;
            this.handleVariantChange(variant);
          });

          const firstImage = this.elements.viewer?.querySelector('[data-media-id] img');
          if (firstImage && firstImage.alt) {
            this.handleVariantChange({ featured_media: { alt: firstImage.alt } });
          }
          this.renderStaffDiagnostics();
          this.playAutoplayVideos();
        });

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        // Delegated so thumbnails added by product-info.js on a variant change work too.
        this.elements.thumbnails.addEventListener('click', (event) => {
          const thumbnail = event.target.closest('[data-target]');
          if (!thumbnail || !this.elements.thumbnails.contains(thumbnail)) return;
          if (!event.target.closest('button')) return;
          this.setActiveMedia(thumbnail.dataset.target, false);
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      // Per-variant galleries (custom.gallery_media) are rendered server-side with only the
      // selected variant's items, so the alt-tag filter below must not touch them.
      get isExplicitGallery() {
        return this.dataset.galleryMode === 'explicit';
      }

      // Staff-only note under the gallery: which mode is active, where the list came from, and in
      // legacy mode which alt tags never match a variant. Shown in the theme editor, on theme
      // previews, and after ?staff=1 (same rule as hide-variants-by-metafield).
      renderStaffDiagnostics() {
        let staff = false;
        try {
          const params = new URLSearchParams(window.location.search);
          if (params.get('staff') === '0') sessionStorage.removeItem('ono_staff');
          else if (params.has('staff')) sessionStorage.setItem('ono_staff', '1');
          staff = Boolean(window.Shopify?.designMode) || params.has('preview_theme_id') || sessionStorage.getItem('ono_staff') === '1';
        } catch (e) {
          staff = Boolean(window.Shopify?.designMode);
        }
        const existing = this.querySelector('.gallery-staff-note');
        if (!staff) {
          existing?.remove();
          return;
        }
        const note = existing || document.createElement('p');
        note.className = 'gallery-staff-note caption';
        note.style.cssText = 'margin:0.8rem 0 0;padding:0.6rem 0.9rem;border:1px dashed rgba(var(--color-foreground),0.4);border-radius:0.4rem;font-size:1.2rem;line-height:1.4;';
        const { galleryMode, galleryCount, galleryVariant, galleryInheritedFrom, galleryIgnoreShared, galleryVariantTags, galleryOwn, galleryShared, galleryNumbered } = this.dataset;
        const parts = [];
        if (galleryMode === 'explicit') {
          parts.push(`Gallery: Variant Media Gallery mode, ${galleryCount} slide${galleryCount === '1' ? '' : 's'} for ${galleryVariant}: variant image + ${galleryOwn} own + ${galleryShared} shared from the Media grid (${galleryNumbered} with a slide number)`);
          if (galleryInheritedFrom) parts.push(`own list inherited from “${galleryInheritedFrom}”`);
          if (galleryIgnoreShared) parts.push('shared images ignored for this variant');
        } else {
          parts.push(`Gallery: legacy alt-tag filter (eam_…) for ${galleryVariant}`);
          if (this.dataset.galleryMigration) {
            parts.push(`Variant Media Gallery migration in progress: ${this.dataset.galleryMigration} colors have a list, legacy until every color does`);
          }
          const variantTags = (galleryVariantTags || '').split(',').filter(Boolean);
          const unmatched = new Set();
          this.elements.viewer?.querySelectorAll('[data-media-id] img').forEach((img) => {
            const tag = this.getFilterTag(img.alt);
            if (!tag) {
              unmatched.add('(no alt)');
              return;
            }
            const shown = variantTags.some((current) => {
              const segments = current.split('_');
              const matchingAlt = segments.length >= 3 ? `eam_all_${segments.slice(2).join('_')}` : null;
              const allMatch = tag.endsWith('_all') && current.startsWith(tag.slice(0, -4));
              return tag === 'eam_all' || tag === current || tag === matchingAlt || allMatch;
            });
            if (!shown) unmatched.add(tag);
          });
          if (unmatched.size) parts.push(`never shown for any variant: ${Array.from(unmatched).join(', ')}`);
        }
        note.textContent = `Staff only. ${parts.join('. ')}.`;
        if (!existing) this.appendChild(note);
      }

      // Extract the eam_ filter tag from alt text, stripping any descriptive text after "|"
      // e.g. "eam_color_blue | ONO Roller in Blue" → "eam_color_blue"
      getFilterTag(alt) {
        if (!alt) return '';
        const tag = alt.split('|')[0].trim().toLowerCase();
        return tag;
      }

      handleVariantChange(variant) {
        // A single-variant product shows its whole Media grid: with nothing to filter between, the
        // alt filter would key off the first image's alt and hide everything else.
        if (!variant || this.isExplicitGallery || this.hasAttribute('data-gallery-single')) return;

        if (variant.featured_media && variant.featured_media.alt) {
          const currentImgAlt = this.getFilterTag(variant.featured_media.alt);

          // Extract the dynamic part from currentImgAlt
          const altParts = currentImgAlt.split('_');
          const matchingAlt = altParts.length >= 3 ? `eam_all_${altParts.slice(2).join('_')}` : null;

          // Get ignoreSharedImages value from selected swatch
          const checkedInput = document.querySelector('.swatch-input__input[type="radio"]:checked');
          const swatchLabel = checkedInput ? checkedInput.nextElementSibling : null;
          const ignoreSharedImages = swatchLabel?.getAttribute('data-ignore-shared-images') === 'true';

          // Hide all thumbnails first
          this.elements.thumbnails?.querySelectorAll('[data-target]').forEach((thumbnail) => {
            const img = thumbnail.querySelector('img');
            if (img) {
              const imgAlt = this.getFilterTag(img.alt);
              // New: check for _all matching
              let allMatch = false;
              if (imgAlt.endsWith('_all')) {
                const allPrefix = imgAlt.slice(0, -4); // remove '_all'
                allMatch = currentImgAlt.startsWith(allPrefix);
              }
              if (ignoreSharedImages) {
                // Hide only 'eam_all', but allow _all prefix matches
                thumbnail.style.display =
                  imgAlt === 'eam_all'
                    ? 'none'
                    : imgAlt === currentImgAlt || imgAlt === matchingAlt || allMatch
                    ? 'block'
                    : 'none';
              } else {
                thumbnail.style.display =
                  imgAlt === 'eam_all' || imgAlt === currentImgAlt || imgAlt === matchingAlt || allMatch
                    ? 'block'
                    : 'none';
              }
            }
          });

          // Hide main viewer images
          this.elements.viewer?.querySelectorAll('[data-media-id]').forEach((media) => {
            const img = media.querySelector('img');
            if (img) {
              const imgAlt = this.getFilterTag(img.alt);
              // New: check for _all matching
              let allMatch = false;
              if (imgAlt.endsWith('_all')) {
                const allPrefix = imgAlt.slice(0, -4); // remove '_all'
                allMatch = currentImgAlt.startsWith(allPrefix);
              }
              if (ignoreSharedImages) {
                // Hide only 'eam_all', but allow _all prefix matches
                media.style.display =
                  imgAlt === 'eam_all'
                    ? 'none'
                    : imgAlt === currentImgAlt || imgAlt === matchingAlt || allMatch
                    ? 'block'
                    : 'none';
              } else {
                media.style.display =
                  imgAlt === 'eam_all' || imgAlt === currentImgAlt || imgAlt === matchingAlt || allMatch
                    ? 'block'
                    : 'none';
              }
            }
          });
        } else {
          // Show all images if no featured media or alt text
          this.elements.thumbnails?.querySelectorAll('[data-target]').forEach((thumbnail) => {
            thumbnail.style.display = 'block';
          });
          this.elements.viewer?.querySelectorAll('[data-media-id]').forEach((media) => {
            media.style.display = 'block';
          });
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail &&
              activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
        this.playAutoplayVideos();
      }

      // Hosted product videos render as autoplaying, muted, looping <video> elements (GIF
      // replacements). Slides adopted from a fetched document on variant change don't always
      // start on their own, so nudge every paused one.
      playAutoplayVideos() {
        this.querySelectorAll('video[data-autoplay]').forEach((video) => {
          window.lazyAutoplayVideo(video, (v) => this.chooseRendition(v));
        });
      }

      // Pick the smallest MP4 rendition that covers the slot (element width × device pixel ratio)
      // from data-renditions (see snippets/product-video-inline.liquid). Runs once per video.
      chooseRendition(video) {
        if (video.dataset.renditionChosen || !video.dataset.renditions) return;
        let renditions = [];
        try {
          renditions = JSON.parse(video.dataset.renditions).filter((r) => r.format === 'mp4' && r.url);
        } catch (e) {
          renditions = [];
        }
        if (!renditions.length) return;
        renditions.sort((a, b) => a.width - b.width);
        // Floor at 720: 480p looks soft at any size, and an unmeasured slot (0px) must not pick it.
        const measured = (video.clientWidth || video.parentElement?.clientWidth || 0) * (window.devicePixelRatio || 1);
        const needed = Math.max(measured, 720);
        const pick = renditions.find((r) => r.width >= needed) || renditions[renditions.length - 1];
        video.dataset.renditionChosen = pick.width;
        video.querySelectorAll('source').forEach((el) => el.remove());
        video.src = pick.url;
        video.load();
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
